import { CreatorRankingPageProps } from './CreatorRankingPage';
import { CreatorRankingItem } from '../../../components/cells/CreatorRankingItem/CreatorRankingItem';
import { map, pipe, toArray, zipWithIndex } from '@fxts/core';
import { CoreUiS } from '../../../../features/Core/outbound/share';
import { SectionTabs } from '../../../../shared/components/atoms/SectionTabs/SectionTabs';
import { SectionTitle } from '../../../components/cells/SectionTitle/SectionTitle';
import { CalendarPlugin } from '../../../components/cells/SectionTitle/plugins';
import { ListView, View, $ } from 'rune-ts';
import { CalendarSelectView } from '../../../components/cells/CalendarView/CalendarView';
import {
  ProductCardView,
  ProductLikeToggleEvent,
} from '../../../components/cells/ProductCard/ProductCardView.ts';
import { CreatorApiS } from '../../../../features/Creator/outbound/share';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { Alert } from '../../../../shared/components/cells/Alert/Alert';
import { preventEscapeErrorJson } from '../../../../shared/util/preventEscapeErrorJson';
import { MarppleShopLoadingView } from '../../../components/atoms/Loading/MarppleShopLoadingView';
import { ICreatorRanking } from '@services/MarppleShop/features/Creator/type';
import { isThisMonth, isThisWeek, startOfWeek, startOfMonth, format, differenceInMinutes } from 'date-fns';
import klass from './CreatorRankingPage.module.scss';

type CreatorRankingTabs = {
  [key in CreatorRankingPageProps['current_tab_id']]: key;
};

class CreatorRankingList extends ListView<CreatorRankingItem> {
  override tagName = 'ul';
  ItemView = CreatorRankingItem;
}

export class CreatorRankingPageController {
  public Loading = new MarppleShopLoadingView();

  public readonly texts: ReturnType<typeof this.makeTexts>;

  public titleView: SectionTitle;

  public tabsView: SectionTabs;

  public sectionTitleView: SectionTitle;

  public creatorRankingListView: CreatorRankingList;

  constructor(private readonly data: CreatorRankingPageProps) {
    this.texts = this.makeTexts(data.current_tab_id);
    const { texts } = this;

    this.titleView = CoreUiS.makeRealTimeRankingTitle(
      data.layout_options.is_mobile,
      'creator_ranking',
      ET('mps2::section_title::sub_title::creator_ranking'),
    );

    this.tabsView = new SectionTabs({
      tabs: [
        {
          id: CreatorRankingPageController.TABS.realtime,
          label: texts.realtime,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.realtime}`,
        },
        {
          id: CreatorRankingPageController.TABS.weekly,
          label: texts.weekly,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.weekly}`,
        },
        {
          id: CreatorRankingPageController.TABS.monthly,
          label: texts.monthly,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.monthly}`,
        },
      ],
      current_tab_id: data.current_tab_id,
      size: data.layout_options.is_mobile ? 'small' : 'medium',
      variant: 'bubble',
    });

    this.sectionTitleView = new SectionTitle({
      plugins:
        data.current_tab_id === 'realtime'
          ? []
          : [
              new CalendarPlugin({
                tab: data.current_tab_id,
                is_mobile: data.layout_options.is_mobile,
                hide_period: true,
                date: data.date ? new Date(data.date) : new Date(),
              }),
            ],
      title_props: {
        title: this.tabsView,
        size: data.layout_options.is_mobile ? 'xSmall' : 'small',
      },
    });

    this.creatorRankingListView = new CreatorRankingList(
      this.makeCreatorRankingItemDataList(data, () => false),
    );
  }

  static TABS: CreatorRankingTabs = {
    realtime: 'realtime',
    weekly: 'weekly',
    monthly: 'monthly',
  };

  private makeCreatorRankingItemDataList(
    data: CreatorRankingPageProps,
    activateFn: (index: number, creator_ranking: ICreatorRanking) => boolean,
  ) {
    return pipe(
      data.creator_rankings,
      zipWithIndex,
      map(([index, creator_ranking]) => ({
        creator: creator_ranking,
        active: activateFn(index, creator_ranking),
        default_product_card_props: {
          user_id: data.layout_options.user?.id,
        },
        is_mobile: data.layout_options.is_mobile,
        store_id: creator_ranking.store_id,
      })),
      toArray,
    );
  }

  public makeUpdateTimeString() {
    const date = new Date(this.data.updated_at);

    if (this.data.current_tab_id === 'weekly' && !isThisWeek(date)) {
      return '';
    } else if (this.data.current_tab_id === 'monthly' && !isThisMonth(date)) {
      return '';
    } else {
      const update_time = format(date, 'yyyy.MM.dd HH:mm');
      const minutes_diff = Math.abs(differenceInMinutes(new Date(this.data.updated_at), new Date()));

      return `${ET('mps2::main::creator_ranking::updated_at', { minute: minutes_diff })} ${update_time}`;
    }
  }

  public makeTexts(current_tab_id: CreatorRankingPageProps['current_tab_id']) {
    const tab_name = ET(`mps2::creator_ranking::${current_tab_id}`);

    return {
      title: ET('mps2::creator_ranking::title_main', { tab: tab_name }),
      title_mo: ET('mps2::creator_ranking::title_mo', { tab: tab_name }),
      sub_title: ET('mps2::creator_ranking::sub_title'),
      realtime: ET('mps2::creator_ranking::realtime'),
      weekly: ET('mps2::creator_ranking::weekly'),
      monthly: ET('mps2::creator_ranking::monthly'),
      list_title: ET('mps2::creator_ranking::list_title'),
      home: ET('mps2::breadcrumbs::home'),
      creator_ranking: ET('mps2::breadcrumbs::creator_ranking'),
    };
  }

  public delegateProductLikeEvent(view: View) {
    view.delegate(ProductLikeToggleEvent, ProductCardView, async (_, cardView) => {
      await cardView.toggleLikeButton();
    });
  }

  private renderRankingList = async (
    date: Date,
    data: CreatorRankingPageProps,
    view: View<CreatorRankingPageProps>,
  ) => {
    try {
      this.Loading.show();
      const { creator_rankings, updated_at } = await CreatorApiS.getCreatorRankings(
        data.current_tab_id,
        date,
      );

      view.data.creator_rankings = creator_rankings;
      if (data.current_tab_id === 'weekly' && !isThisWeek(date)) {
        view.data.updated_at = date;
      } else if (data.current_tab_id === 'monthly' && !isThisMonth(date)) {
        view.data.updated_at = date;
      } else {
        view.data.updated_at = updated_at;
      }

      this.creatorRankingListView.set(this.makeCreatorRankingItemDataList(view.data, (index) => index === 0));
      this.creatorRankingListView.redraw();

      $(view.element()).find(`.${klass.updated_at}`)?.setTextContent(this.makeUpdateTimeString());
    } catch (e) {
      ConfigSentryF.error(e);
      return Alert.open(
        preventEscapeErrorJson({
          title: ET('mps2::common::error_title'),
          message: ET('mps2::common::error_message'),
        }),
      );
    } finally {
      this.Loading.hide();
    }
  };

  public delegateCalendarChangeEvent(data: CreatorRankingPageProps, view: View<CreatorRankingPageProps>) {
    view.addEventListener(CalendarSelectView.ChangeDateEvent, (event) => {
      let next_date: Date | undefined;

      if (data.current_tab_id === 'monthly') {
        next_date = startOfMonth(event.detail.date);
      } else if (data.current_tab_id === 'weekly') {
        next_date = startOfWeek(event.detail.date);
      }

      if (next_date) {
        const url = new URL(location.href);
        url.searchParams.set('date', next_date.toISOString());
        url.searchParams.delete('storeId');
        history.pushState(null, '', url.toString());

        this.renderRankingList(next_date, data, view);
      }
    });
  }

  public handlePopstate = (data: CreatorRankingPageProps, view: View<CreatorRankingPageProps>) => {
    const params = new URLSearchParams(location.search);
    const date_param = params.get('date');

    if (date_param) {
      view.data.date = date_param;
      const next_date = new Date(date_param);

      this.renderRankingList(next_date, data, view);

      const calendar_plugin = this.sectionTitleView.pluginViews.at(0) as unknown as CalendarSelectView;
      calendar_plugin.setDate(next_date);
    }
  };
}
